import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, Hourglass, WarningCircle, ArrowRight, WhatsappLogo, House } from "@phosphor-icons/react";
import { getCheckoutStatus } from "../lib/api";
import { openWhatsapp } from "../lib/whatsapp";
import Logo from "../components/Logo";

const MAX_POLLS = 10;
const POLL_MS = 2500;

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "polling", data: null, attempts: 0 });
  const timerRef = useRef(null);

  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: { detail: "Missing session_id" }, attempts: 0 });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data, attempts });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data, attempts });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ phase: "timeout", data, attempts });
          return;
        }
        setState({ phase: "polling", data, attempts });
        timerRef.current = setTimeout(tick, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ phase: "error", data: { detail: e?.response?.data?.detail || String(e) }, attempts });
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  const goHome = () => navigate("/");

  return (
    <div className="min-h-screen bg-[#F4F3ED] text-[#0A0A0A] flex flex-col">
      <header className="border-b border-[#D4D0C5] px-5 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Logo className="h-10 w-auto" />
          <span className="font-display font-black text-sm tracking-tight">
            GREAT EAGLE TECH
          </span>
        </Link>
        <button
          onClick={goHome}
          data-testid="back-home-btn"
          className="inline-flex items-center gap-2 font-mono-acc text-[11px] tracking-[0.25em] uppercase border border-[#0A0A0A] px-3 py-2 hover:bg-[#0A0A0A] hover:text-[#F4F3ED] transition-colors"
        >
          <House size={14} /> Home
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="max-w-xl w-full border border-[#0A0A0A] bg-[#F4F3ED] p-8 lg:p-12">
          {state.phase === "polling" && (
            <div data-testid="payment-polling" className="flex flex-col items-start">
              <Hourglass size={56} weight="duotone" className="text-[#D9381E] animate-pulse mb-5" />
              <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A] mb-2">
                Status · {state.attempts}/{MAX_POLLS}
              </div>
              <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-3">
                Confirming your payment…
              </h1>
              <p className="font-body text-[#4A4A4A]">
                Hang tight — Stripe is settling the transaction. This page will
                update automatically once payment is confirmed.
              </p>
            </div>
          )}

          {state.phase === "paid" && (
            <div data-testid="payment-paid" className="flex flex-col items-start">
              <CheckCircle size={64} weight="duotone" className="text-[#D9381E] mb-5" />
              <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-2">
                Payment received
              </div>
              <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-3">
                Welcome, trader.
              </h1>
              <p className="font-body text-[#4A4A4A] mb-6">
                Your seat in <strong>Cohort 15 / 2026</strong> is confirmed. Our
                team will reach out within 24 hours with onboarding and the
                WhatsApp group invite. Access your course materials on the
                Student Portal below.
              </p>

              <div className="grid grid-cols-2 gap-4 w-full mb-6">
                <div className="border border-[#0A0A0A] p-3">
                  <div className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]">
                    Amount
                  </div>
                  <div className="font-mono-acc font-bold text-lg">
                    ₹{(state.data?.amount || 40000).toLocaleString("en-IN")}
                  </div>
                </div>
                <div className="border border-[#0A0A0A] p-3">
                  <div className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]">
                    Cohort starts
                  </div>
                  <div className="font-mono-acc font-bold text-lg">31 August 2026</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <a
                  href="https://courses.greateagle.co.in/learn"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="payment-student-portal-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-[#FFB703] px-5 py-3 font-body text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#D9381E] hover:text-white transition-colors"
                >
                  Access Student Portal <ArrowRight size={14} weight="bold" />
                </a>
                <button
                  onClick={() => openWhatsapp("Hi, I just paid for the MP & OF course.")}
                  data-testid="payment-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-[#0A0A0A] px-5 py-3 font-body text-sm uppercase tracking-[0.22em] font-bold cursor-pointer"
                >
                  <WhatsappLogo size={18} weight="fill" /> Ping us on WhatsApp
                </button>
              </div>
            </div>
          )}

          {(state.phase === "expired" || state.phase === "timeout") && (
            <div data-testid="payment-issue" className="flex flex-col items-start">
              <WarningCircle size={56} weight="duotone" className="text-[#D9381E] mb-5" />
              <h1 className="font-display font-black text-3xl tracking-tighter mb-3">
                {state.phase === "expired"
                  ? "Payment session expired"
                  : "Still confirming…"}
              </h1>
              <p className="font-body text-[#4A4A4A] mb-6">
                {state.phase === "expired"
                  ? "Your Stripe checkout session expired before payment could be completed. Please try again."
                  : "We couldn't confirm payment status in time. If you completed the payment, please WhatsApp us and we'll verify within minutes."}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={goHome}
                  className="border border-[#0A0A0A] px-5 py-3 font-body text-sm uppercase tracking-[0.22em]"
                >
                  Back to site
                </button>
                <button
                  onClick={() => openWhatsapp()}
                  className="bg-[#25D366] text-[#0A0A0A] px-5 py-3 font-body text-sm uppercase tracking-[0.22em] font-bold inline-flex items-center gap-2 cursor-pointer"
                >
                  <WhatsappLogo size={16} weight="fill" /> Help
                </button>
              </div>
            </div>
          )}

          {state.phase === "error" && (
            <div data-testid="payment-error" className="flex flex-col items-start">
              <WarningCircle size={56} weight="duotone" className="text-[#D9381E] mb-5" />
              <h1 className="font-display font-black text-3xl tracking-tighter mb-3">
                Something went wrong
              </h1>
              <p className="font-body text-[#4A4A4A] mb-6">
                {state.data?.detail || "Could not fetch payment status."}
              </p>
              <button
                onClick={goHome}
                className="border border-[#0A0A0A] px-5 py-3 font-body text-sm uppercase tracking-[0.22em]"
              >
                Back to site
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
